import React, { useCallback, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

import Button from 'components_new/atoms/Button'
import Icon from 'components_new/atoms/Icon'

import ErrorPlaceholder from 'components_new/molecules/ErrorPlaceholder'

import ExportTablesList from 'components_new/organisms/ExportTablesList'
import ExportTablesListHero from 'components_new/organisms/ExportTablesListHero'
import MetricsDialog from 'components_new/organisms/dialogs/MetricsDialog'

import PageLayout from 'components_new/templates/PageLayout'

import { useIsMobile } from 'utils/hooks'
import { useTranslation } from 'language/translator'
import { useExportTableActions } from 'redux/actions/ExportTables'
import { useIsCreatingExportTable } from 'redux/hooks/ExportTables'
import { useAllowAccessExportTables } from 'redux/hooks/Permissions'
import { getMetricTitle } from 'utils/metric'

const ExportTables = () => {
  const { lang, t, translateEnum } = useTranslation()
  const isMobile = useIsMobile()
  const history = useHistory()
  const { tryGetAllExportTables, tryCreateExportTable } =
    useExportTableActions()
  const isCreatingExportTable = useIsCreatingExportTable()
  const allowAccess = useAllowAccessExportTables()

  const [searchTerm, setSearchTerm] = useState<string | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)

  useEffect(() => {
    if (allowAccess) {
      tryGetAllExportTables()
    }
  }, [tryGetAllExportTables, allowAccess])

  const handleCreate = useCallback(() => {
    setDialogOpen(true)
  }, [])

  if (!allowAccess) {
    return (
      <ErrorPlaceholder
        code={401}
        body={t('export_tables', 'no_access_body')}
        actions={
          <Button
            variant="text"
            onClick={() => history.push('/')}
            startIcon={<Icon name="ArrowBackOutlined" />}
          >
            {t('global', 'home')}
          </Button>
        }
      />
    )
  }

  return (
    <>
      <MetricsDialog
        availableSteps={['variant', 'slices']}
        creating={isCreatingExportTable}
        handleOnAdd={(metric, settings) => {
          const title = getMetricTitle(metric, settings, lang, translateEnum)

          tryCreateExportTable(
            {
              data: {
                kpi_id: metric.id,
                slices: settings.slices,
                title,
                variant_kpi_template_id: settings.variant_kpi_template_id
              }
            },
            (err, data) => {
              if (!err) {
                setDialogOpen(false)

                if (data) {
                  history.push(`/export-tables/${data.id}`)
                }
              }
            }
          )
        }}
        onClose={() => setDialogOpen(false)}
        open={dialogOpen}
        title={t('export_tables', 'choose_metric')}
        variant="export_tables"
      />
      <PageLayout
        content={{
          disablePadding: true,
          fullWidth: true,
          hero: (
            <ExportTablesListHero
              search={searchTerm}
              setSearch={setSearchTerm}
            />
          )
        }}
        topbar={{
          actions: !isMobile ? (
            <Button
              onClick={handleCreate}
              startIcon={<Icon name="AddOutlined" />}
              variant="text"
            >
              {t('global', 'create')}
            </Button>
          ) : null,
          breadcrumbs: [
            {
              href: '/export-tables',
              icon: { name: 'Table' },
              title: t('export_tables', 'all_tables')
            }
          ]
        }}
        variant="feed"
      >
        <ExportTablesList sx={{ pb: 8 }} search={searchTerm} />
      </PageLayout>
    </>
  )
}

export default ExportTables
